import { useEffect, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { useUserContext } from '@/lib/auth/useUserContext';
import { apiFetch } from '@/lib/api/client';
import { getWidget } from '@/widgets/registry';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';

interface LayoutWidget {
  key: string;
  title: string;
  config?: Record<string, unknown> | null;
}

interface DashboardLayoutResponse {
  role: string;
  widgets: LayoutWidget[];
}

function tier2RoleKey(groups: readonly string[]): 'barista' | 'shift_lead' | null {
  if (groups.includes('ROLE:shift_lead')) return 'shift_lead';
  if (groups.includes('ROLE:barista')) return 'barista';
  return null;
}

export function Dashboard() {
  const { tier, primaryRole, groups, loading: meLoading } = useUserContext();

  const [layout, setLayout] = useState<DashboardLayoutResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const layoutKey = tier === 'store_ops' ? tier2RoleKey(groups) : primaryRole;

  useEffect(() => {
    if (!layoutKey) return;
    let cancelled = false;
    apiFetch<DashboardLayoutResponse>(`/dashboard-layouts/${layoutKey}`)
      .then((l) => {
        if (!cancelled) setLayout(l);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [layoutKey]);

  const showSkeleton = (meLoading || (!!layoutKey && !layout)) && !error;

  return (
    <div className="space-y-6">
      <PageHeader title="Dashboard" description="Operations at a glance for your stores." />

      {error ? <p className="text-sm text-destructive">Could not load layout: {error}</p> : null}

      {!layoutKey && !meLoading ? (
        <EmptyState
          icon={ShieldAlert}
          title="No role assigned"
          description="You're signed in but not assigned to any Qargo role yet. Contact your admin to get store access."
        />
      ) : null}

      {showSkeleton ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full rounded-xl" />
          ))}
        </div>
      ) : null}

      {layout ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {layout.widgets.map((w, idx) => {
            const Cmp = getWidget(w.key);
            if (!Cmp) {
              return (
                <div
                  key={`${w.key}-${idx}`}
                  className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground"
                >
                  Unknown widget: <code>{w.key}</code>
                </div>
              );
            }
            return <Cmp key={`${w.key}-${idx}`} title={w.title} />;
          })}
        </div>
      ) : null}
    </div>
  );
}
